import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useApi, useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  ControlledNumberInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Label,
  LoadingButton,
  SearchSelect,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@fortem-monorepo/react-ui'

import { useMarketSummaryContext } from '@/pages/instruments/market-summary/market-summary-context'
import { useAddMetricToDisplayMutation } from '@/pages/instruments/market-summary/market-summary.mutations'

import { AddMetricFormValues, addMetricDefaults, addMetricSchema, getAddMetricParams } from './add-metric.schema'

export interface AddMetricDialogProps extends DialogProps {
  onClose: () => void
  mutationParams: { display_id: number }
}

export const AddMetricDialog: React.FC<AddMetricDialogProps> = ({ mutationParams, onClose, ...props }) => {
  const form = useFormWithSchema({ mode: 'all', defaultValues: addMetricDefaults }, addMetricSchema)
  const { eventsQuery } = useMarketSummaryContext()
  const api = useApi()
  const addMetricMutation = useAddMetricToDisplayMutation()
  const metricType = form.watch('metric_type')
  const periodType = form.watch('period_type')
  const { reset, trigger } = form
  const pending = form.formState.isSubmitting || addMetricMutation.isPending

  const returnFrequenciesQuery = useQuery({
    queryKey: ['market-summary', 'return-frequencies'],
    queryFn: () => api.marketSummary.getReturnFrequencies(),
    enabled: Boolean(props.open) && metricType === 'calculation' && periodType === 'frequency',
  })

  useEffect(() => {
    if (props.open) reset(addMetricDefaults)
  }, [props.open, mutationParams.display_id, reset])

  useEffect(() => {
    void trigger()
  }, [metricType, periodType, trigger])

  const changeSelection = (selection: Partial<Pick<AddMetricFormValues, 'metric_type' | 'period_type'>>) => {
    const values = { ...form.getValues(), ...selection }
    reset(
      {
        ...values,
        price_offset_days: values.metric_type === 'price' ? values.price_offset_days : null,
        event_id: values.metric_type === 'calculation' && values.period_type === 'event' ? values.event_id : null,
        return_frequency:
          values.metric_type === 'calculation' && values.period_type === 'frequency' ? values.return_frequency : null,
      },
      { keepDefaultValues: true },
    )
  }

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await addMetricMutation.mutateAsync(getAddMetricParams(data, mutationParams.display_id))
      onClose()
    } catch {
      // The mutation displays the error; retain the selection for a retry.
      return
    }
  })

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open && !pending) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Metric</DialogTitle>
          <DialogDescription>Add a value to the current display.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <Tabs
            value={metricType}
            onValueChange={(value) => {
              if (value === 'price' || value === 'calculation') {
                changeSelection({ metric_type: value })
              }
            }}
            className="gap-4"
          >
            <TabsList className="w-full" aria-label="Value type">
              <TabsTrigger value="price" disabled={pending}>
                Price Value
              </TabsTrigger>
              <TabsTrigger value="calculation" disabled={pending}>
                Calculation Value
              </TabsTrigger>
            </TabsList>
            <TabsContent value="price">
              <FormField
                control={form.control}
                name="price_offset_days"
                render={() => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>Offset (Days)</FormLabel>
                      <FormError />
                    </div>
                    <ControlledNumberInput
                      name="price_offset_days"
                      min={0}
                      allowNegative={false}
                      decimalScale={0}
                      disabled={pending}
                      placeholder="Enter offset in days"
                    />
                  </FormItem>
                )}
              />
            </TabsContent>
            <TabsContent value="calculation" className="space-y-4">
              <FormField
                control={form.control}
                name="calculation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Calculation</FormLabel>
                    <SearchSelect
                      value={field.value}
                      setValue={(value) => {
                        if (value === 'return') field.onChange(value)
                      }}
                      options={[{ label: 'Return', value: 'return' }]}
                      disabled={pending}
                    />
                  </FormItem>
                )}
              />
              <Tabs
                value={periodType}
                className="gap-4"
                onValueChange={(value) => {
                  if (value === 'event' || value === 'frequency') {
                    changeSelection({ period_type: value })
                  }
                }}
              >
                <Label>Period</Label>
                <TabsList className="w-full" aria-label="Period type">
                  <TabsTrigger value="event" disabled={pending}>
                    Event
                  </TabsTrigger>
                  <TabsTrigger value="frequency" disabled={pending}>
                    Return Frequency
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="event">
                  <FormField
                    control={form.control}
                    name="event_id"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel>Event</FormLabel>
                          <FormError />
                        </div>
                        <SearchSelect
                          placeholder="Select Event"
                          value={field.value?.toString()}
                          setValue={(value) => field.onChange(value ? parseInt(value, 10) : null)}
                          loading={eventsQuery.isLoading}
                          disabled={pending || eventsQuery.isLoading || eventsQuery.isError}
                          options={(eventsQuery.data ?? []).map((event) => ({
                            value: event.event_id.toString(),
                            label: event.name,
                          }))}
                        />
                        {eventsQuery.isError && (
                          <p className="text-sm text-destructive" role="alert">
                            Unable to load events.
                          </p>
                        )}
                      </FormItem>
                    )}
                  />
                </TabsContent>
                <TabsContent value="frequency">
                  <FormField
                    control={form.control}
                    name="return_frequency"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel>Return Frequency</FormLabel>
                          <FormError />
                        </div>
                        <SearchSelect
                          placeholder="Select Return Frequency"
                          value={field.value ?? undefined}
                          setValue={(value) => field.onChange(value ?? null)}
                          loading={returnFrequenciesQuery.isLoading}
                          disabled={pending || returnFrequenciesQuery.isLoading || returnFrequenciesQuery.isError}
                          options={(returnFrequenciesQuery.data ?? []).map((frequency) => ({
                            value: frequency,
                            label: frequency,
                          }))}
                        />
                        {returnFrequenciesQuery.isError && (
                          <p className="text-sm text-destructive" role="alert">
                            Unable to load return frequencies.
                          </p>
                        )}
                      </FormItem>
                    )}
                  />
                </TabsContent>
              </Tabs>
            </TabsContent>
          </Tabs>
        </Form>
        <DialogFooter>
          <div className="flex items-center gap-2">
            <Button size="sm" type="button" variant="destructive" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <LoadingButton size="sm" onClick={onSubmit} disabled={!form.formState.isValid || pending}>
              Create
            </LoadingButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
